import React, { useState, useEffect, useRef } from "react";
import { Zap, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  calcBlendedSimilarity,
  getSimilarityTier,
  getSharedStylesByDimension,
} from "../utils/similarity";
import { countryFlag, countryName } from "../utils/countries";

// Similarity between the viewer and another traveler, framed as kinship, not a grade:
// a tier label ("Kindred traveler" / "Similar style") when the score clears a threshold,
// shared-style chips as always-visible evidence, and never a low or zero score.
// viewer/target: user objects with travelStyles + optional behaviorStats.
// compact: tier chip only, shared-tag evidence lives in the popover (tight layouts)
function SimilarityBadge({ viewer, target, align = "left", compact = false }) {
  const { t, i18n } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef(null);

  useEffect(() => {
    if (!showInfo) return;
    const handleClick = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setShowInfo(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [showInfo]);

  const result = calcBlendedSimilarity(viewer, target);
  const sharedGroups = getSharedStylesByDimension(viewer?.travelStyles, target?.travelStyles);
  const sharedFlat = sharedGroups.flatMap((g) => g.tags);
  if (!result && !sharedFlat.length) return null;

  const tier = getSimilarityTier(result?.score);
  const sharedCountries = result?.sharedCountries || [];
  if (!tier && (compact || !sharedFlat.length)) return null;

  const popover = showInfo && (
    <div
      className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-1.5 z-20 w-64 bg-th-surface border border-th-border rounded-xl p-3 shadow-lg shadow-black/20`}
    >
      <p className="text-xs text-th-text leading-relaxed">
        {t("profile.matchInfo")}
        {result?.behaviorUsed && ` ${t("profile.matchInfoBehavior")}`}
      </p>
      {sharedGroups.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {sharedGroups.map((g) => (
            <div key={g.dimension} className="flex flex-wrap items-center gap-1">
              <span className="text-[9px] font-mono uppercase tracking-wider text-th-muted mr-0.5">
                {t(`editProfile.${g.dimension}Label`)}
              </span>
              {g.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] bg-th-accent-dim text-th-accent border border-th-accent-border px-2 py-0.5 rounded-md font-mono"
                >
                  {tag}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      {sharedGroups.length === 0 && (
        <p className="text-[11px] text-th-muted mt-1.5 font-mono">{t("profile.noSharedStyles")}</p>
      )}
      {sharedCountries.length > 0 && (
        <p className="text-[11px] text-th-text-sec mt-2 leading-relaxed">
          {t("profile.bothPinned")}{" "}
          {sharedCountries
            .slice(0, 4)
            .map((c) => `${countryFlag(c)} ${countryName(c, i18n.language)}`)
            .join(" · ")}
        </p>
      )}
    </div>
  );

  if (tier) {
    return (
      <div className="relative" ref={infoRef}>
        <div className="inline-flex flex-wrap items-center gap-1.5 bg-th-accent-dim border border-th-accent-border px-3 py-1 rounded-lg">
          <Zap size={12} className="text-th-accent" />
          <span className="text-xs font-semibold text-th-accent">
            {tier === "kindred" ? t("profile.kindredTraveler") : t("profile.similarStyle")}
          </span>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="ml-0.5 text-th-accent hover:text-th-accent/80 transition-colors"
          >
            <Info size={12} />
          </button>
        </div>
        {!compact && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {sharedFlat.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="text-[11px] bg-th-accent-dim text-th-accent border border-th-accent-border px-2 py-0.5 rounded-md font-mono"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {popover}
      </div>
    );
  }

  // Below the tier threshold: no verdict on the person, just the shared-tag evidence
  return (
    <div className="relative" ref={infoRef}>
      <div className="inline-flex flex-wrap items-center gap-1">
        <span className="text-[11px] text-th-muted font-mono mr-0.5">{t("profile.youBoth")}</span>
        {sharedFlat.slice(0, 4).map((tag) => (
          <span
            key={tag}
            className="text-[11px] bg-th-surface text-th-text-sec border border-th-border px-2 py-0.5 rounded-md font-mono"
          >
            {tag}
          </span>
        ))}
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="ml-0.5 text-th-muted hover:text-th-text-sec transition-colors"
        >
          <Info size={10} />
        </button>
      </div>
      {popover}
    </div>
  );
}

export default SimilarityBadge;
